import React, { useState } from 'react';
import { Toolbar } from '@mui/x-data-grid-pro';
import {
  GridToolbarColumnsButton,
  GridToolbarExportButton,
} from '../../utils/gridToolbarParts.js';
import { CustomQuickFilter } from '../../utils/CustomQuickFilter.js';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import OutlinedInput from '@mui/material/OutlinedInput';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Filter options
const stageOptions = [
  { value: 'all', label: 'All Stages' },
  { value: 'BAR', label: 'BAR' },
  { value: 'SCAR', label: 'SCAR' },
];

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Unsettled', label: 'Active' },
  { value: 'Settled', label: 'Settled' },
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 150,
    },
  },
};

/**
 * EvidenceComparisonToolbar - Custom toolbar for Evidence Comparison table
 * Left side: Column visibility, Stage filter, Status filter, Quick filter
 * Right side: Select All buttons and Set Evidence button
 */
const EvidenceComparisonToolbar = React.memo(function EvidenceComparisonToolbar(props) {
  const { 
    activeStage, 
    setActiveStage, 
    statusFilter, 
    setStatusFilter,
    onSetEvidence, 
    isSettingEvidence = false,
    onSetMinOffer,
    onSetRecommended,
    onSelectAllSupporting,
    muniCode,
    onSelectAllSales,
    onSelectAllAssessments,
    areAllSalesSelected,
    areAllAssessmentsSelected,
    onSendOffersClick,
    isSendingOffersAndWorkups = false,
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSetRecommended = () => {
    onSetRecommended?.();
    handleClose();
  };

  const handleSelectAllSupporting = () => {
    onSelectAllSupporting?.();
    handleClose();
  };

  const handleSetMinOffer = () => {
    onSetMinOffer?.();
    handleClose();
  };

  const handleSelectAllSales = () => {
    onSelectAllSales?.();
    handleClose();
  };

  const handleSelectAllAssessments = () => {
    onSelectAllAssessments?.();
    handleClose();
  };

  return (
    <Toolbar sx={{ 
      px: '8px', 
      py: '4px',
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center' 
    }}>
      {/* Left section: filtering and searching */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
        {/* Column Visibility Selector */}
        <GridToolbarColumnsButton />
        
        <GridToolbarExportButton options={{
          fileName: muniCode ? `${muniCode}_evidence_comparison` : 'evidence_comparison',
          delimiter: ',',
        }} />
        
        {/* Stage Filter Dropdown */}
        <FormControl size="small" sx={{ minWidth: 120, '& .MuiInputBase-root': { height: 32 } }}>
          <InputLabel id="stage-select-label" sx={{ bgcolor: 'transparent !important', zIndex: '1 !important', pointerEvents: 'none' }}>
            Stage
          </InputLabel>
          <Select
            labelId="stage-select-label"
            id="stage-select"
            value={activeStage || 'all'}
            onChange={(e) => setActiveStage?.(e.target.value)}
            input={<OutlinedInput label="Stage" />}
            renderValue={(selected) => {
              const found = stageOptions.find(opt => opt.value === selected);
              return found ? found.label : selected;
            }}
            MenuProps={MenuProps}
          >
            {stageOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{ py: 0 }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* Status Filter Dropdown */}
        <FormControl size="small" sx={{ minWidth: 120, '& .MuiInputBase-root': { height: 32 } }}>
          <InputLabel id="status-select-label" sx={{ bgcolor: 'transparent !important', zIndex: '1 !important', pointerEvents: 'none' }}>
            Status
          </InputLabel>
          <Select
            labelId="status-select-label"
            id="status-select"
            value={statusFilter || 'all'}
            onChange={(e) => setStatusFilter?.(e.target.value)}
            input={<OutlinedInput label="Status" />}
            renderValue={(selected) => {
              const found = statusOptions.find(opt => opt.value === selected);
              return found ? found.label : selected;
            }}
            MenuProps={MenuProps}
          >
            {statusOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{ py: 0 }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* Quick search filter */}
        <CustomQuickFilter />
      </Box>

      {/* Right section: Bulk Updates menu and Set Evidence button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClick}
          size="small"
          endIcon={<ArrowDropDownIcon />}
          sx={{
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        >
          Bulk Updates
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleSelectAllSales}>
            {areAllSalesSelected ? 'Deselect All Sales' : 'Select All Sales'}
          </MenuItem>
          <MenuItem onClick={handleSelectAllAssessments}>
            {areAllAssessmentsSelected ? 'Deselect All Assessments' : 'Select All Assessments'}
          </MenuItem>
          <MenuItem onClick={handleSetRecommended}>
            Set Rec Evidence
          </MenuItem>
          <MenuItem onClick={handleSelectAllSupporting}>
            Select All Supporting
          </MenuItem>
          <MenuItem onClick={handleSetMinOffer}>
            Set Min Offers
          </MenuItem>
        </Menu>
        <Button
          variant="contained"
          onClick={onSetEvidence}
          size="small"
          disabled={isSettingEvidence}
          startIcon={isSettingEvidence ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0',
            },
            '&:disabled': {
              backgroundColor: '#1976d2',
              opacity: 0.7,
            },
          }}
        >
          {isSettingEvidence ? 'Setting...' : 'Set Evidence'}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={onSendOffersClick}
          size="small"
          disabled={isSendingOffersAndWorkups}
          startIcon={isSendingOffersAndWorkups ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            minWidth: 120,
            whiteSpace: 'nowrap',
            backgroundColor: '#9c27b0',
            '&:hover': {
              backgroundColor: '#7b1fa2',
            },
            '&:disabled': {
              backgroundColor: '#9c27b0',
              opacity: 0.7,
            },
          }}
        >
          {isSendingOffersAndWorkups ? 'Sending…' : 'Send Offers'}
        </Button>
      </Box>
    </Toolbar>
  );
});

export default EvidenceComparisonToolbar;

